import classNames from "classnames";
import { Formik, Field, Form } from "formik";
import { useCallback, useEffect, useState } from "react";
import isPlainObject from "lodash/isPlainObject";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { actions } from "../../../organisms/Drawer/drawers.reducers";
import Drawer, { selectSelectedDrawer } from "../../../organisms/Drawer";
import {
  selectCollection,
  selectSelectedProperty,
  selectCollections,
  updateCollection,
} from "../../../../pages/DataModels";
import { actions as dataModelActions } from "../../../../pages/DataModels/dataModels.reducers";
import {
  OPERATIONS,
  DRAWER_SIZE,
  INTERFACES,
  CUSTOM,
  DATA_SOURCE,
  DROPDOWN_TYPE,
} from "../drawers.constants";

import { renderField, getFieldType, getFieldLabel } from "../drawers.helpers";
import {
  BUTTON_TEXT,
  INDEX,
  REGEX,
  EMPTY_STRING,
  EMPTY_ARRAY,
  ALERT_MESSAGES,
  VALIDATION_MESSAGES,
  IMAGE_SIZE_EXCEED_MESSAGE,
} from "../../../../constants";
import Alert from "../../Alert";
import { getBase64FromFile } from "../../../../helpers";
import { selectPredefinedProperties } from "../../../../pages/Applications";
import get from "lodash/get";
import { alertRedIcon, crossIcon } from "../../../../assets/icons";
import Spinner, { SPINNER_NAMES } from "../../Spinner";

const { FIRST, SECOND } = INDEX;
const { setSelectedDrawer } = actions;
const { setSelectedProperty } = dataModelActions;

const { SUBMIT } = BUTTON_TEXT;
const {
  CHECKBOX,
  FILE,
  IMAGE,
  EMAIL,
  REPEATER_FIELDS,
  SELECT,
  JSON_TYPE,
  INPUT,
  TEXT_AREA,
} = INTERFACES;
const { FULL } = DRAWER_SIZE;
const PROPERTY_FIELDS = {
  KEY: "key",
  VALUES: "values",
  OBJECT: "object",
  TYPE: "type",
};
const MAX_FILE_SIZE = 10240;
const DEFAULT_VALUES = { name: EMPTY_STRING };
const { PROPERTY_NAME, EMAIL: EMAIL_REGEX } = REGEX;
const { UPDATE_COLLECTION } = SPINNER_NAMES;
const {
  PROPERTY_REQUIRED,
  VALID_PROPERTY_NAME,
  CHECK_REQUIRED_FIELD,
  DEFAULT_VALUE_REQUIRED,
  VALID_EMAIL,
  VALIDATE_INTEGER,
  INVALID_NUMBER,
  INTEGER,
  REFERENCE_TABLE_REQUIRED,
  REFERENCE_TABLE_COLUMN_REQUIRED,
  CHECK_OPTIONS,
  VALUE_REQUIRED,
  CHECK_SELECTED_CHOICES,
  SELECT_A_CHOICE,
  CHECK_EMAIL,
  MIN_CHAR_LENGTH,
  MAX_CHAR_LENGTH_TWENTY_ONE,
} = VALIDATION_MESSAGES;

const { EXTENSION_NOT_SUPPORTED, PROPERTY_NAME_MUST_BE_UNIQUE } =
  ALERT_MESSAGES;

const ManagePropertyDrawer = () => {
  const dispatch = useDispatch();
  const selectedCollection = useSelector(selectCollection);
  const selectedProperty = useSelector(selectSelectedProperty);
  const selectedDrawer = useSelector(selectSelectedDrawer);
  const collectionProperties = useSelector(selectPredefinedProperties);
  const { data: collections } = useSelector(selectCollections);

  const [dropdownValueType, setDropdownValueType] = useState();
  const [currentStep, setCurrentStep] = useState(FIRST);
  const [property, setProperty] = useState();
  const [collection, setCollection] = useState();
  const [initialValues, setInitialValues] = useState(DEFAULT_VALUES);
  const [currentProperty, setCurrentProperty] = useState(selectedProperty);
  const [alertData, setAlertData] = useState();
  const [predefinedProperties, setPredefinedProperties] = useState(EMPTY_ARRAY);
  const [requiredSettings, setRequiredSettings] = useState(EMPTY_ARRAY);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [shouldSubmit, setShouldSubmit] = useState(false);

  const handlePropertySelection = useCallback(
    (selectedProperty) => {
      if (selectedProperty) {
        setProperty(selectedProperty);
        setCurrentProperty(selectedProperty);
        setCurrentStep(SECOND);
        setInitialValues(selectedProperty);
      } else setInitialValues({ ...DEFAULT_VALUES, ...property });
      setDropdownValueType(
        get(selectedProperty, ["schema", "dropdownType"], CUSTOM)
      );
    },
    [property]
  );

  useEffect(() => {
    collectionProperties && setPredefinedProperties(collectionProperties);
  }, [collectionProperties]);

  useEffect(() => {
    selectedProperty && setCurrentProperty(selectedProperty);
  }, [selectedProperty]);

  useEffect(() => {
    handlePropertySelection(currentProperty);
  }, [handlePropertySelection, currentProperty]);

  useEffect(() => {
    setCollection(selectedCollection);
  }, [selectedCollection]);

  useEffect(() => {
    if (!initialValues.meta) return;
    const filteredSettings = Object.keys(initialValues.meta).filter(
      (value) => getFieldType(value, initialValues.meta.interface) === CHECKBOX
    );
    setRequiredSettings(filteredSettings);
  }, [initialValues]);

  const handleClose = useCallback(() => {
    dispatch(setSelectedDrawer(null));
    dispatch(setSelectedProperty(null));
  }, [dispatch]);

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(PROPERTY_REQUIRED)
      .matches(PROPERTY_NAME, VALID_PROPERTY_NAME)
      .min(3, MIN_CHAR_LENGTH)
      .max(21, MAX_CHAR_LENGTH_TWENTY_ONE),
    schema: Yup.object({
      defaultValue: Yup.string()
        .test(
          CHECK_REQUIRED_FIELD,
          DEFAULT_VALUE_REQUIRED,
          (value, context) => {
            const isRequired = context.from[SECOND].value.meta?.isRequired;
            return !isRequired || value;
          }
        )
        .test(
          CHECK_EMAIL,
          VALID_EMAIL,
          (value) =>
            initialValues.meta?.interface !== EMAIL ||
            !value ||
            EMAIL_REGEX.test(value)
        )
        .test(VALIDATE_INTEGER, INVALID_NUMBER, (value, context) => {
          if (context.parent.type === INTEGER) return Number.isInteger(+value);
          return true;
        })
        .test("check-json", "Enter a valid json.", (value, context) => {
          if (value && context.parent.type === JSON_TYPE)
            try {
              return isPlainObject(JSON.parse(value));
            } catch {
              return false;
            }
          return true;
        }),
      referenceTableId: Yup.string().test(
        CHECK_REQUIRED_FIELD,
        REFERENCE_TABLE_REQUIRED,
        (value) => (dropdownValueType === DATA_SOURCE ? value : true)
      ),
      referenceTableColumn: Yup.string().test(
        CHECK_REQUIRED_FIELD,
        REFERENCE_TABLE_COLUMN_REQUIRED,
        (value) => (dropdownValueType === DATA_SOURCE ? value : true)
      ),
    }),
    meta: Yup.object({
      options: Yup.array().test(
        CHECK_OPTIONS,
        VALUE_REQUIRED,
        (value) =>
          initialValues.meta?.interface !== REPEATER_FIELDS ||
          dropdownValueType === DATA_SOURCE ||
          !value.some((each) => !each.value)
      ),
      selectedChoices: Yup.string().test(
        CHECK_SELECTED_CHOICES,
        SELECT_A_CHOICE,
        (value) => initialValues.meta?.interface !== IMAGE || value
      ),
    }),
  });

  const updateValuesForDropdown = useCallback(
    (values) => {
      const selectedReferenceCollection = collections.find(
        (collection) => collection.id === values.schema.referenceTableId
      );
      const filteredColumn = get(
        selectedReferenceCollection,
        ["schema", "properties"],
        EMPTY_ARRAY
      ).find(
        (column) => column.schema.key === values.schema.referenceTableColumn
      );
      const updatedValues = {
        ...values,
        schema: {
          ...values.schema,
          type:
            values.schema?.dropdownType === CUSTOM
              ? "varchar(255)"
              : get(filteredColumn, ["schema", "type"], EMPTY_STRING),
        },
      };
      return updatedValues;
    },
    [collections]
  );

  const checkIsDuplicateName = useCallback(
    (propertyName, filteredProperties) =>
      filteredProperties.some((property) => property.name === propertyName),
    []
  );

  const handleOnSubmit = useCallback(
    (values) => {
      const properties = selectedCollection.schema.properties;
      const filteredProperties = selectedProperty
        ? properties.filter(
            (property) => property.name !== selectedProperty.name
          )
        : properties;
      const propertyName = values.name;
      const hasError =
        checkIsDuplicateName(propertyName, filteredProperties) ||
        propertyName === EMPTY_STRING;

      if (hasError) {
        setAlertData({ message: PROPERTY_NAME_MUST_BE_UNIQUE });
      } else {
        let newProperty = {
          ...values,
          schema: {
            ...values.schema,
            key: values.name.replaceAll(" ", "_"),
          },
        };
        delete newProperty.image;
        if (
          newProperty.meta.interface === JSON_TYPE &&
          newProperty.schema.defaultValue === EMPTY_STRING
        ) {
          newProperty.schema.defaultValue = "{}";
        }
        if (
          (newProperty.meta.interface === IMAGE ||
            newProperty.meta.interface === FILE) &&
          !!newProperty.schema.defaultValue
        ) {
          const parsedDataOfImageOrFile = JSON.parse(
            newProperty.schema.defaultValue
          );
          newProperty = {
            ...newProperty,
            schema: {
              ...newProperty.schema,
              defaultValue: parsedDataOfImageOrFile,
            },
          };
        }
        if (newProperty.meta.interface === REPEATER_FIELDS)
          newProperty = updateValuesForDropdown(newProperty);

        dispatch(
          updateCollection({
            id: collection.id,
            data: {
              changeSet: {
                ...collection,
                schema: {
                  properties: [newProperty],
                },
              },
              operation: selectedProperty
                ? OPERATIONS.UPDATE
                : OPERATIONS.CREATE,
              ...(selectedProperty && {
                collection: {
                  ...collection,
                  schema: { properties: [selectedProperty] },
                },
              }),
            },
          })
        ).then((res) => res.payload && handleClose());
      }
    },
    [
      selectedCollection?.schema?.properties,
      selectedProperty,
      checkIsDuplicateName,
      updateValuesForDropdown,
      dispatch,
      collection,
      handleClose,
    ]
  );

  const shouldRenderSettings = (each, interfaceType) =>
    getFieldType(each, interfaceType) === CHECKBOX;

  const handleOnClose = (handleReset) => {
    handleReset();
    handleClose();
  };

  const removeField = (type, values) => {
    const removeFields = [PROPERTY_FIELDS.TYPE, PROPERTY_FIELDS.KEY];
    switch (values.meta.interface) {
      case REPEATER_FIELDS:
        if (dropdownValueType === CUSTOM) {
          removeFields.push("referenceTableColumn");
          removeFields.push("referenceTableId");
        } else removeFields.push("defaultValue");
        break;
      case SELECT:
        removeFields.push("defaultValue");
        break;
      default:
    }
    return !removeFields.includes(type);
  };

  const getFieldOptions = (key, options, values) => {
    if (key === "referenceTableId") {
      const filteredOptions = collections.reduce((options, collection) => {
        if (collection.name !== selectedCollection.name)
          options = [
            ...options,
            {
              name: collection.name,
              value: collection.id,
            },
          ];
        return options;
      }, []);
      return filteredOptions;
    } else if (key === "referenceTableColumn") {
      const selectedReferenceCollection = collections.find(
        (collection) =>
          collection.id ===
          get(values, ["schema", "referenceTableId"], EMPTY_STRING)
      );
      const filteredOptions = get(
        selectedReferenceCollection,
        ["schema", "properties"],
        EMPTY_ARRAY
      ).reduce((columnOptions, column) => {
        if (column.name !== "id")
          columnOptions = [
            ...columnOptions,
            {
              name: column.name,
              value: column.schema.key,
            },
          ];
        return columnOptions;
      }, []);
      return filteredOptions;
    } else if (key === "dropdownType") return DROPDOWN_TYPE;
    else return options;
  };

  const handleFileUpload = async (e, setFieldValue, values) => {
    setShouldSubmit(true);
    const file = e.target.files[FIRST];
    const base64Url = await getBase64FromFile(file);
    const fileSizeInKiloBytes = file.size / 1024;
    const fileExtension = file.name.split(".").pop();

    const isImageTypeError =
      values.meta.interface === IMAGE &&
      !values.meta.selectedChoices.includes(`.${fileExtension},`);

    const isSizeError = fileSizeInKiloBytes > MAX_FILE_SIZE;
    const errorMessage = isImageTypeError
      ? EXTENSION_NOT_SUPPORTED
      : IMAGE_SIZE_EXCEED_MESSAGE;

    if (isImageTypeError || isSizeError) {
      setAlertData({ message: errorMessage });
    } else {
      setFieldValue(
        e.target.name,
        JSON.stringify({ name: file.name, base64Url })
      );

      const { name, meta, schema } = values;
      const { type, key } = schema;

      const currentPropertyObject = {
        name,
        meta,
        schema: {
          type,
          key,
          defaultValue: JSON.stringify({ name: file.name, base64Url }),
        },
      };
      setCurrentProperty(currentPropertyObject);
    }
  };

  const getDrawerSize = (step) => (step === FIRST ? FULL : EMPTY_STRING);

  const getDrawerHeading = (step) =>
    step === FIRST ? "Create a Property" : property.meta.description;

  const getDrawerSubHeading = (step) =>
    step === FIRST
      ? "Create a new property by selecting the property type"
      : "Configure the property setting";

  const checkShouldShowTemplates = (step) => step === FIRST;

  const shouldRenderChoices = (values) => get(values, "meta.choices");

  const getWarningMessage = (values) =>
    !values.meta.selectedChoices && (
      <div className="-mt-2 mb-2 text-gray-500 flex items-center gap-1">
        <img src={alertRedIcon} alt="warning" className="w-4 h-4" />
        <span>Please select a choice</span>
      </div>
    );

  const handleChoiceChange = (
    event,
    each,
    values,
    handleChange,
    setFieldValue
  ) => {
    handleChange(event);
    const updatedChoices = event.target.checked
      ? values.meta.selectedChoices.concat(`.${each}, `)
      : values.meta.selectedChoices.replace(`.${each}, `, EMPTY_STRING);
    setFieldValue(`meta.selectedChoices`, updatedChoices);
    const defaultValue = values.schema.defaultValue;
    if (defaultValue) {
      const fileName = defaultValue.name || JSON.parse(defaultValue).name;
      const imageExtension = fileName.split(".").pop();
      const shouldUpdateValue =
        each === imageExtension && !event.target.checked;
      if (shouldUpdateValue) setFieldValue(`schema.defaultValue`, EMPTY_STRING);
    }
  };

  const getSubmissionState = (
    shouldSubmit,
    isSpinnerActive,
    isValid,
    dirty
  ) => {
    return shouldSubmit ? false : isSpinnerActive || !isValid || !dirty;
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleReset,
        handleBlur,
        setFieldValue,
        isValid,
        setFieldTouched,
        dirty,
      }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn-primary-light mr-1"
              type="button"
              onClick={() =>
                currentStep === FIRST || selectedProperty
                  ? handleOnClose(handleReset)
                  : setCurrentStep(FIRST)
              }
            >
              {currentStep === SECOND && !selectedProperty
                ? "Previous"
                : "Cancel"}
            </button>
            {currentStep === SECOND && (
              <button
                data-testid="submit-button"
                className="btn btn-primary"
                type={SUBMIT}
                disabled={getSubmissionState(
                  shouldSubmit,
                  isSpinnerActive,
                  isValid,
                  dirty
                )}
              >
                <Spinner
                  name={UPDATE_COLLECTION}
                  setIsSpinnerActive={setIsSpinnerActive}
                  persistSize
                >
                  {SUBMIT}
                </Spinner>
              </button>
            )}
          </div>
        );
        let schema = values.schema;
        if (values.schema && !values.schema.defaultValue) {
          const allowedInterface = [FILE, IMAGE, INPUT, TEXT_AREA];
          if (allowedInterface.includes(values.meta.interface)) {
            let updatedSchema = {
              ...values.schema,
              defaultValue: EMPTY_STRING,
            };
            schema = updatedSchema;
          }
        }
        const removeLogo = () => {
          setShouldSubmit(true);
          const currentPropertyObject = {
            name: currentProperty.name,
            meta: currentProperty.meta,
            schema: {
              type: currentProperty.schema.type,
              key: currentProperty.schema.key,
            },
          };
          setCurrentProperty(currentPropertyObject);
        };

        return (
          <Form>
            <Drawer
              position="right"
              size={getDrawerSize(currentStep)}
              heading={getDrawerHeading(currentStep)}
              subHeading={getDrawerSubHeading(currentStep)}
              selectedDrawer={selectedDrawer}
              handleClose={handleClose}
              handleReset={handleReset}
              footer={footer}
            >
              {checkShouldShowTemplates(currentStep) ? (
                <div className="scrollbar flex-1 overflow-auto p-6">
                  <div className="grid grid-cols-12 gap-6">
                    {predefinedProperties.map((each) => (
                      <div
                        className="col-span-6 sm:col-span-4 md:col-span-3 lg:col-span-2"
                        key={each.meta.description}
                      >
                        <button
                          type={SUBMIT}
                          disabled={each.meta.hidden}
                          onClick={() => handlePropertySelection(each)}
                          className="w-full p-4 bg-gray-900 rounded-lg text-left hover:ring-2 hover:ring-indigo-700 hover:shadow-md hover:shadow-indigo-700 hover:bg-gray-800"
                        >
                          <div className="icon mb-4">
                            <img
                              src={`data:image/svg+xml,${each.image}`}
                              className="w-full"
                              alt={each.subscription}
                            />
                          </div>
                          <span
                            className="text-sm text-gray-300 font-medium"
                            data-testid={`property-${each.meta.description}`}
                          >
                            {each.meta.description}
                          </span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="scrollbar flex-1 overflow-auto">
                  <div className="space-y-4 p-6 border-b border-gray-700">
                    {alertData && (
                      <div className="my-2">
                        <Alert
                          type={alertData.type}
                          message={alertData.message}
                          handleOnClose={setAlertData}
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="text-sm font-medium text-gray-50 mb-1 block">
                        Property Name
                      </label>
                      <Field
                        name="name"
                        type="text"
                        data-testid="property-name"
                        className={classNames("form-control border-2", {
                          "border-red-600": touched.name && errors.name,
                        })}
                        placeholder="Property Name"
                        value={values.name}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        autoComplete="off"
                        autoFocus={true}
                      />
                      {touched.name && errors.name && (
                        <div
                          className="text-sm font-medium text-red-500"
                          data-testid="name-error"
                        >
                          {errors.name}
                        </div>
                      )}
                    </div>
                    {values &&
                      schema &&
                      Object.keys(schema).map(
                        (each) =>
                          !shouldRenderSettings(each, values.meta.interface) &&
                          removeField(each, values) && (
                            <div key={each}>
                              {[FILE, IMAGE].includes(values.meta.interface) &&
                                get(values, "schema.defaultValue") && (
                                  <button
                                    type="button"
                                    className="ml-auto -mb-5 rounded-md hover:bg-gray-900 flex"
                                    onClick={removeLogo}
                                  >
                                    <img
                                      alt="cross"
                                      src={crossIcon}
                                      className="w-4"
                                    />
                                  </button>
                                )}
                              {renderField({
                                value: schema[`${each}`],
                                name: `schema.${each}`,
                                label: getFieldLabel(each),
                                handleBlur,
                                handleChange: async (e) => {
                                  if (
                                    values.meta.interface === FILE ||
                                    values.meta.interface === IMAGE
                                  )
                                    await handleFileUpload(
                                      e,
                                      setFieldValue,
                                      values
                                    );
                                  else if (each === "dropdownType")
                                    setDropdownValueType(e.target.value);
                                  else handleChange(e);
                                },
                                interfaceType: getFieldType(
                                  each,
                                  values.meta.interface
                                ),
                                options: getFieldOptions(
                                  each,
                                  values.meta.options,
                                  values
                                ),
                                setFieldValue,
                                setFieldTouched,
                                errors,
                                touched,
                                shouldShowValidationMessage: true,
                                selectedChoices: values.meta.selectedChoices,
                                disabled:
                                  values.meta.interface === IMAGE &&
                                  !values.meta.selectedChoices,
                                type: schema.type,
                                shouldUpdateType: true,
                              })}
                            </div>
                          )
                      )}
                    {shouldRenderChoices(values) && (
                      <div className="form-group text-sm font-medium text-white">
                        {getWarningMessage(values)}
                        <div className="flex justify-between mb-2">
                          <span>Choices</span>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              data-testid="select-all"
                              onClick={() => {
                                Object.keys(values.meta.choices).forEach(
                                  (key) =>
                                    setFieldValue(`meta.choices.${key}`, true)
                                );
                                setFieldValue(
                                  `meta.selectedChoices`,
                                  property.meta.selectedChoices
                                );
                              }}
                            >
                              All
                            </button>
                            /
                            <button
                              type="button"
                              data-testid="select-none"
                              onClick={() => {
                                Object.keys(values.meta.choices).forEach(
                                  (key) =>
                                    setFieldValue(`meta.choices.${key}`, false)
                                );
                                setFieldValue(
                                  `meta.selectedChoices`,
                                  EMPTY_STRING
                                );
                                setFieldValue(
                                  `schema.defaultValue`,
                                  EMPTY_STRING
                                );
                              }}
                            >
                              None
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap">
                          {Object.keys(values.meta.choices).map((each) => (
                            <div key={each} className="w-1/3">
                              {renderField({
                                value: values.meta.choices[each],
                                name: `meta.choices.${each}`,
                                label: each,
                                interfaceType: CHECKBOX,
                                handleChange: (event) =>
                                  handleChoiceChange(
                                    event,
                                    each,
                                    values,
                                    handleChange,
                                    setFieldValue
                                  ),
                              })}
                            </div>
                          ))}
                        </div>
                        {get(touched, "meta.selectedChoices") &&
                          get(errors, "meta.selectedChoices") && (
                            <div
                              className="text-sm font-medium text-red-500 -mt-4"
                              data-testid="choices-error"
                            >
                              {errors.meta.selectedChoices}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    {requiredSettings.length > FIRST && (
                      <>
                        <h6 className="text-sm font-medium text-gray-50 mb-2">
                          Settings
                        </h6>
                        {requiredSettings.map((each) => (
                          <div key={each}>
                            {renderField({
                              value: values.meta[`${each}`],
                              name: `meta.${each}`,
                              label: getFieldLabel(each),
                              handleBlur,
                              handleChange,
                              options: values.meta.options,
                              interfaceType: getFieldType(
                                each,
                                values.meta.interface
                              ),
                              referenceTable: values.schema.referenceTable,
                              setFieldValue,
                            })}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </Drawer>
          </Form>
        );
      }}
    </Formik>
  );
};
export default ManagePropertyDrawer;
